/**
 * Create Ticket Modal Component
 * Modal wrapper for ticket creation
 */

import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { TicketForm } from './TicketForm';
import { useCreateTicket } from '@/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { TicketCreate } from '@/types';

interface CreateTicketModalProps {
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
}

export function CreateTicketModal({ projectId, isOpen, onClose }: CreateTicketModalProps) {
  const { mutate: createTicket, isPending } = useCreateTicket(projectId);
  const queryClient = useQueryClient();

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleSubmit = (data: TicketCreate) => {
    createTicket(data, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['tickets'] });
        onClose();
      },
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-scale-in">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Create New Ticket</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-6">
            <TicketForm projectId={projectId}
              onSubmit={handleSubmit}
              isSubmitting={isPending}
              submitLabel="Create Ticket"
              onCancel={onClose}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
